import { css } from '@emotion/react'
import { IArticle } from 'api/contentful/getArticle'
import { IArticleListItem } from 'api/contentful/getArticleList'
import { ArticleCard } from 'components/articleCard/ArticleCard'
import {
  ArticleContentImage,
  SArticle,
  SArticleContent,
  SArticleHeader,
  SMoreArticles,
} from './ArticleContent.styled'
import { ArticleShareLinks } from 'components/articles/articleShareLinks/ArticleShareLinks'
import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import { ContentfulContent } from 'components/content/contentfulContent/ContentfulContent'
import { Icon } from 'components/icon'
import { Caption, Text, Title, Title_h1_default, Title_h2_default } from 'components/Typography'
import { format, parseISO } from 'date-fns'
import { useTranslation } from 'next-i18next'
import { encodeQuery } from 'utils/fetcher'
import { BREAKPOINTS, pxToRem } from 'utils/styled'

export function ArticleContent(props: {
  article: IArticle
  isInside?: boolean
  otherArticles?: IArticleListItem[]
}) {
  const { t } = useTranslation()

  return (
    <Block css={{ overflow: 'hidden' }}>
      <div>
        <SArticle>
          <SArticleHeader>
            {props.article.coverImage && (
              <ArticleContentImage
                src={encodeQuery(props.article.coverImage?.url, {
                  w: 900,
                })}
                alt={props.article.coverImage?.title}
              />
            )}
            <Caption css={{ color: 'var(--c-blue500)', marginBottom: '0.5625rem' }}>
              {props.article.category?.name}
            </Caption>

            <Title
              variant={Title_h1_default}
              css={{ marginBottom: '0.875rem', wordBreak: 'break-word' }}
            >
              {props.article.title}
            </Title>

            <div
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Text>{format(parseISO(props.article.sys.firstPublishedAt), 'MMM-dd yyyy')}</Text>
              <ArticleShareLinks path={`/articles/${props.article.slug}`} />
            </div>
          </SArticleHeader>

          <SArticleContent>
            {props.article.content && (
              <ContentfulContent
                data={props.article.content}
                imageQuery={{
                  w: 900,
                }}
              />
            )}
          </SArticleContent>

          {props.article.siteId != null && (
            <section>
              <Title
                variant={Title_h2_default}
                as="h2"
                css={css`
                  margin-top: ${pxToRem(120)}rem;
                  margin-bottom: 1rem;

                  @media ${BREAKPOINTS.MD.max} {
                    margin-top: ${pxToRem(60)}rem;
                  }
                `}
              >
                {t('common.articles.page.cta_title')}
              </Title>

              <Button
                type="button"
                variant="text"
                size="large"
                leftIcon={<Icon name="Plus" />}
                href="/store/site/[siteId]"
                as={`/store/site/${props.article.siteId}`}
              >
                {t('common.get_started')}
              </Button>
            </section>
          )}

          <ArticleShareLinks
            path={`/articles/${props.article.slug}`}
            css={css`
              align-items: center;
              margin: ${pxToRem(120)}rem 0;

              @media ${BREAKPOINTS.MD.max} {
                margin: ${pxToRem(40)}rem 0;
                flex-wrap: wrap;
              }
            `}
          />
        </SArticle>

        <SMoreArticles>
          {props.otherArticles
            ?.filter((article) => article.sys.id !== props.article.sys.id)
            ?.slice(0, 3)
            ?.map((article) => (
              <ArticleCard
                key={article.sys.id}
                title={article.title}
                coverImage={article.coverImage?.url}
                content={article.content}
                href={encodeQuery(`/articles/${article.slug}`, { inside: props.isInside })}
              />
            ))}
        </SMoreArticles>
      </div>
    </Block>
  )
}
